import React, { useEffect, useState, Children, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../lib/supabase';
import { ChevronRight, ArrowLeft, CheckCircle2, Loader2, MessageCircle, X, Smartphone, UtensilsCrossed, BarChart3, Crown, Zap, QrCode } from 'lucide-react';
// --- Background Elements ---
const FLOATING_FOODS = ['🍔', '🍕', '🍣', '☕', '🍟', '🌮', '🍰', '🥗', '🍩', '🥑'];
const BACKGROUND_WORDS = ['Pedidos', 'WhatsApp', 'Delivery', 'Marketplace', 'KDS', 'Tracking', 'Operación', 'Tecnología', 'Restaurantes', 'Crecimiento', 'Control', 'Inventario'];
// --- Form Types ---
type BusinessType = 'Restaurante' | 'Cafetería' | 'Pizzería' | 'Comida rápida' | 'Panadería' | 'Otro';
type MainGoal = 'Más pedidos' | 'Mejor control de cocina' | 'Menú digital' | 'Delivery / express' | 'Orden y operación' | 'Todo lo anterior';
interface FormData {
  owner_name: string;
  restaurant_name: string;
  whatsapp: string;
  social_handle: string;
  business_type: BusinessType | '';
  uses_whatsapp_orders: boolean | null;
  has_digital_menu: boolean | null;
  main_goal: MainGoal[];
}
export function OnboardingPage() {
  const [step, setStep] = useState(0); // 0 = Hero, 1-5 = Form, 6 = Success
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showHowItWorks, setShowHowItWorks] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    owner_name: '',
    restaurant_name: '',
    whatsapp: '',
    social_handle: '',
    business_type: '',
    uses_whatsapp_orders: null,
    has_digital_menu: null,
    main_goal: []
  });
  // --- Handlers ---
  const handleNext = () => setStep(s => Math.min(s + 1, 6));
  const handleBack = () => setStep(s => Math.max(s - 1, 0));
  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError(null);
    try {
      const cleanWhatsapp = formData.whatsapp.replace(/\D/g, '');
      if (!formData.owner_name || !formData.restaurant_name || !cleanWhatsapp || !formData.business_type) {
        throw new Error('Faltan campos obligatorios');
      }
      const {
        error
      } = await supabase.from('restaurant_applications').insert([{
        owner_name: formData.owner_name.trim(),
        restaurant_name: formData.restaurant_name.trim(),
        whatsapp: cleanWhatsapp,
        social_handle: formData.social_handle?.trim() || null,
        business_type: formData.business_type,
        uses_whatsapp_orders: formData.uses_whatsapp_orders ?? false,
        has_digital_menu: formData.has_digital_menu ?? false,
        main_goal: formData.main_goal.join(', ') || null,
        status: 'pending'
      }]);
      if (error) {
        console.error('❌ Error guardando aplicación:', error);
        throw error;
      }
      console.log('✅ Aplicación guardada correctamente');
      // Open WhatsApp with pre-filled message
      const message = encodeURIComponent(`Hola, acabo de aplicar a NOIZA.\nRestaurante: ${formData.restaurant_name}`);
      window.open(`https://wa.me/50688118105?text=${message}`, '_blank');
      setStep(6); // Success
    } catch (err: any) {
      console.error('❌ Error en submit:', err);
      setError('Hubo un error al enviar tu solicitud. Por favor intenta de nuevo.');
    } finally {
      setIsSubmitting(false);
    }
  };
  // --- Step Validation ---
  const isStepValid = () => {
    switch (step) {
      case 1:
        return formData.owner_name.trim().length > 0 && formData.restaurant_name.trim().length > 0;
      case 2:
        return formData.whatsapp.trim().length >= 8;
      case 3:
        return formData.business_type !== '';
      case 4:
        return formData.uses_whatsapp_orders !== null && formData.has_digital_menu !== null;
      case 5:
        return formData.main_goal.length > 0;
      default:
        return true;
    }
  };
  // --- Render Helpers ---
  const renderFloatingBackground = () => <div className="fixed inset-0 overflow-hidden pointer-events-none z-0" data-id="element-5785">
      {/* Dark gradient base */}
      <div className="absolute inset-0 bg-[#0B0B0B]" data-id="element-5786" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(201,163,78,0.15)_0%,rgba(11,11,11,1)_70%)]" data-id="element-5787" />

      {/* Floating Words */}
      {BACKGROUND_WORDS.map((word, i) => <motion.div key={`word-${i}`} className="absolute text-white/5 font-black text-4xl md:text-6xl lg:text-8xl whitespace-nowrap select-none" initial={{
      x: Math.random() * window.innerWidth,
      y: Math.random() * window.innerHeight,
      rotate: -15 + Math.random() * 30
    }} animate={{
      x: [null, Math.random() * window.innerWidth],
      y: [null, Math.random() * window.innerHeight]
    }} transition={{
      duration: 40 + Math.random() * 40,
      repeat: Infinity,
      repeatType: 'reverse',
      ease: 'linear'
    }} data-id="element-5788">
          {word}
        </motion.div>)}

      {/* Floating Foods */}
      {FLOATING_FOODS.map((food, i) => <motion.div key={`food-${i}`} className="absolute text-4xl md:text-6xl opacity-40 blur-[1px]" initial={{
      x: Math.random() * window.innerWidth,
      y: Math.random() * window.innerHeight,
      scale: 0.5 + Math.random() * 1
    }} animate={{
      y: [null, Math.random() * window.innerHeight],
      rotate: [0, 360]
    }} transition={{
      y: {
        duration: 20 + Math.random() * 20,
        repeat: Infinity,
        repeatType: 'reverse',
        ease: 'easeInOut'
      },
      rotate: {
        duration: 30 + Math.random() * 30,
        repeat: Infinity,
        ease: 'linear'
      }
    }} data-id="element-5789">
          {food}
        </motion.div>)}
    </div>;
  const renderHowItWorksModal = () => <AnimatePresence data-id="element-5790">
      {showHowItWorks && <motion.div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} data-id="element-5791">
          {/* Backdrop */}
          <motion.div className="fixed inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setShowHowItWorks(false)} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} data-id="element-5792" />

          {/* Modal Content */}
          <motion.div className="relative z-10 w-full max-w-3xl mx-4 my-8 sm:my-16 bg-[#141414] border border-white/10 rounded-3xl overflow-hidden shadow-2xl" initial={{
        scale: 0.9,
        opacity: 0,
        y: 40
      }} animate={{
        scale: 1,
        opacity: 1,
        y: 0
      }} exit={{
        scale: 0.9,
        opacity: 0,
        y: 40
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 200
      }} data-id="element-5793">
            {/* Header */}
            <div className="sticky top-0 z-10 bg-[#141414]/95 backdrop-blur-md border-b border-white/10 px-6 py-5 flex items-center justify-between" data-id="element-5794">
              <div data-id="element-5795">
                <h2 className="text-2xl font-bold text-white" data-id="element-5796">
                  ¿Cómo funciona NOIZA?
                </h2>
                <p className="text-sm text-white/50 mt-1" data-id="element-5797">
                  Explicado de forma simple
                </p>
              </div>
              <button onClick={() => setShowHowItWorks(false)} className="p-2 hover:bg-white/10 rounded-xl transition-colors" data-id="element-5798">
                <X className="w-6 h-6 text-white/60" data-id="element-5799" />
              </button>
            </div>

            <div className="p-6 sm:p-8 space-y-10" data-id="element-5800">
              {/* What is NOIZA */}
              <div data-id="element-5801">
                <h3 className="text-lg font-bold text-[#C9A34E] mb-3 flex items-center gap-2" data-id="element-5802">
                  <Zap className="w-5 h-5" data-id="element-5803" />
                  ¿Qué es NOIZA?
                </h3>
                <p className="text-white/70 leading-relaxed" data-id="element-5804">
                  NOIZA es una plataforma digital para restaurantes. En vez de
                  recibir pedidos por WhatsApp de forma desordenada, tus
                  clientes escanean un{' '}
                  <strong className="text-white" data-id="element-5805">código QR</strong>, ven tu menú
                  en su celular, hacen su pedido, y este llega directo a tu
                  cocina organizado. Sin confusiones, sin errores, sin estrés.
                </p>
              </div>

              {/* How it works step by step */}
              <div data-id="element-5806">
                <h3 className="text-lg font-bold text-[#C9A34E] mb-4 flex items-center gap-2" data-id="element-5807">
                  <Smartphone className="w-5 h-5" data-id="element-5808" />
                  Paso a paso
                </h3>
                <div className="space-y-4" data-id="element-5809">
                  {[{
                num: '1',
                title: 'Subimos tu menú',
                desc: 'Nos pasas tu menú (fotos, precios, categorías) y nosotros lo montamos en la plataforma. Queda bonito y profesional.'
              }, {
                num: '2',
                title: 'Te damos un QR y un link',
                desc: 'Tu restaurante tiene su propia página web con tu menú. Tus clientes pueden acceder escaneando un QR en la mesa o desde un link en tus redes.'
              }, {
                num: '3',
                title: 'El cliente hace su pedido',
                desc: 'Tu cliente ve el menú, elige lo que quiere, personaliza opciones (tamaño, extras, salsas) y confirma su pedido desde su celular.'
              }, {
                num: '4',
                title: 'El pedido llega a tu cocina',
                desc: 'En tu pantalla de cocina (KDS) aparece el pedido automáticamente. Sin papelitos, sin gritos, sin confusiones. Lo marcas como listo cuando sale.'
              }, {
                num: '5',
                title: 'Tú controlas todo',
                desc: 'Desde tu panel de admin ves las ventas del día, métricas, puedes pausar la cocina si estás lleno, activar/desactivar platillos, y más.'
              }].map(step => <div key={step.num} className="flex gap-4" data-id="element-5810">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#C9A34E]/20 text-[#C9A34E] font-bold text-sm flex items-center justify-center" data-id="element-5811">
                        {step.num}
                      </div>
                      <div data-id="element-5812">
                        <p className="font-semibold text-white" data-id="element-5813">{step.title}</p>
                        <p className="text-sm text-white/60 mt-0.5" data-id="element-5814">
                          {step.desc}
                        </p>
                      </div>
                    </div>)}
                </div>
              </div>

              {/* Plans */}
              <div data-id="element-5815">
                <h3 className="text-lg font-bold text-[#C9A34E] mb-2 flex items-center gap-2" data-id="element-5816">
                  <Crown className="w-5 h-5" data-id="element-5817" />
                  Nuestros Planes
                </h3>
                <p className="text-white/50 text-sm mb-6" data-id="element-5818">
                  Elegí el que mejor se ajuste a tu restaurante. Siempre podés
                  escalar después.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-5819">
                  {/* Fase 1 */}
                  <div className="bg-white/5 border border-white/10 rounded-2xl p-5 space-y-3" data-id="element-5820">
                    <div className="flex items-center gap-2" data-id="element-5821">
                      <QrCode className="w-5 h-5 text-[#C9A34E]" data-id="element-5822" />
                      <h4 className="font-bold text-white text-sm" data-id="element-5823">
                        Menú Digital Fase 1
                      </h4>
                    </div>
                    <p className="text-xs text-white/50 leading-relaxed" data-id="element-5824">
                      Lo esencial para arrancar digital.
                    </p>
                    <ul className="space-y-2 text-sm text-white/70" data-id="element-5825">
                      <li className="flex items-start gap-2" data-id="element-5826">
                        <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" data-id="element-5827" />
                        <span data-id="element-5828">Tu menú digital con fotos y precios</span>
                      </li>
                      <li className="flex items-start gap-2" data-id="element-5829">
                        <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" data-id="element-5830" />
                        <span data-id="element-5831">Código QR para mesas o redes</span>
                      </li>
                      <li className="flex items-start gap-2" data-id="element-5832">
                        <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" data-id="element-5833" />
                        <span data-id="element-5834">Link propio de tu restaurante</span>
                      </li>
                      <li className="flex items-start gap-2" data-id="element-5835">
                        <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" data-id="element-5836" />
                        <span data-id="element-5837">Configuración inicial incluida</span>
                      </li>
                    </ul>
                    <div className="pt-2 border-t border-white/5" data-id="element-5838">
                      <p className="text-[10px] uppercase tracking-wider text-white/30 font-medium" data-id="element-5839">
                        Ideal para
                      </p>
                      <p className="text-xs text-white/50" data-id="element-5840">
                        Restaurantes que quieren mostrar su menú de forma
                        profesional
                      </p>
                    </div>
                  </div>

                  {/* Fase 2 */}
                  <div className="bg-[#C9A34E]/5 border border-[#C9A34E]/20 rounded-2xl p-5 space-y-3 relative" data-id="element-5841">
                    <div className="absolute -top-2.5 right-4 bg-[#C9A34E] text-[#0B0B0B] text-[10px] font-bold px-3 py-0.5 rounded-full uppercase tracking-wider" data-id="element-5842">
                      Popular
                    </div>
                    <div className="flex items-center gap-2" data-id="element-5843">
                      <UtensilsCrossed className="w-5 h-5 text-[#C9A34E]" data-id="element-5844" />
                      <h4 className="font-bold text-white text-sm" data-id="element-5845">
                        Menú Digital Fase 2
                      </h4>
                    </div>
                    <p className="text-xs text-white/50 leading-relaxed" data-id="element-5846">
                      Menú + sistema de pedidos real.
                    </p>
                    <ul className="space-y-2 text-sm text-white/70" data-id="element-5847">
                      <li className="flex items-start gap-2" data-id="element-5848">
                        <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" data-id="element-5849" />
                        <span data-id="element-5850">Todo lo de Fase 1</span>
                      </li>
                      <li className="flex items-start gap-2" data-id="element-5851">
                        <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" data-id="element-5852" />
                        <span data-id="element-5853">Pedidos directos desde el menú</span>
                      </li>
                      <li className="flex items-start gap-2" data-id="element-5854">
                        <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" data-id="element-5855" />
                        <span data-id="element-5856">Pantalla de cocina (KDS)</span>
                      </li>
                      <li className="flex items-start gap-2" data-id="element-5857">
                        <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" data-id="element-5858" />
                        <span data-id="element-5859">Reportes básicos de ventas</span>
                      </li>
                    </ul>
                    <div className="pt-2 border-t border-[#C9A34E]/10" data-id="element-5860">
                      <p className="text-[10px] uppercase tracking-wider text-white/30 font-medium" data-id="element-5861">
                        Ideal para
                      </p>
                      <p className="text-xs text-white/50" data-id="element-5862">
                        Restaurantes que quieren recibir pedidos organizados y
                        controlar su cocina
                      </p>
                    </div>
                  </div>

                  {/* Completo */}
                  <div className="bg-white/5 border border-white/10 rounded-2xl p-5 space-y-3" data-id="element-5863">
                    <div className="flex items-center gap-2" data-id="element-5864">
                      <BarChart3 className="w-5 h-5 text-[#C9A34E]" data-id="element-5865" />
                      <h4 className="font-bold text-white text-sm" data-id="element-5866">
                        Menú Digital Completo
                      </h4>
                    </div>
                    <p className="text-xs text-white/50 leading-relaxed" data-id="element-5867">
                      El sistema integral para crecer.
                    </p>
                    <ul className="space-y-2 text-sm text-white/70" data-id="element-5868">
                      <li className="flex items-start gap-2" data-id="element-5869">
                        <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" data-id="element-5870" />
                        <span data-id="element-5871">Todo lo de Fase 2</span>
                      </li>
                      <li className="flex items-start gap-2" data-id="element-5872">
                        <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" data-id="element-5873" />
                        <span data-id="element-5874">Reportes avanzados y métricas</span>
                      </li>
                      <li className="flex items-start gap-2" data-id="element-5875">
                        <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" data-id="element-5876" />
                        <span data-id="element-5877">Programa de lealtad para clientes</span>
                      </li>
                      <li className="flex items-start gap-2" data-id="element-5878">
                        <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" data-id="element-5879" />
                        <span data-id="element-5880">Inventario y control de stock</span>
                      </li>
                    </ul>
                    <div className="pt-2 border-t border-white/5" data-id="element-5881">
                      <p className="text-[10px] uppercase tracking-wider text-white/30 font-medium" data-id="element-5882">
                        Ideal para
                      </p>
                      <p className="text-xs text-white/50" data-id="element-5883">
                        Restaurantes que quieren el paquete completo: operación,
                        datos y fidelización
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Marketplace Connection */}
              <div className="bg-gradient-to-br from-[#C9A34E]/10 to-transparent border border-[#C9A34E]/20 rounded-2xl p-6" data-id="element-5884">
                <h3 className="text-lg font-bold text-[#C9A34E] mb-3 flex items-center gap-2" data-id="element-5885">
                  <Crown className="w-5 h-5" data-id="element-5886" />
                  Marketplace NOIZA — Más visibilidad para tu restaurante
                </h3>
                <p className="text-white/70 leading-relaxed mb-4" data-id="element-5887">
                  Además de tu menú digital propio, al tener cualquier plan
                  activo con NOIZA tu restaurante aparece automáticamente en
                  nuestro{' '}
                  <strong className="text-white" data-id="element-5888">
                    Marketplace gastronómico
                  </strong>{' '}
                  en <strong className="text-white" data-id="element-5889">noizacr.com</strong>. Esto
                  significa que clientes nuevos que no te conocen pueden
                  descubrirte, ver tu menú y hacerte pedidos directamente.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-5890">
                  <div className="flex items-start gap-2" data-id="element-5891">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" data-id="element-5892" />
                    <span className="text-sm text-white/70" data-id="element-5893">
                      Tu restaurante listado en el marketplace de NOIZA
                    </span>
                  </div>
                  <div className="flex items-start gap-2" data-id="element-5894">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" data-id="element-5895" />
                    <span className="text-sm text-white/70" data-id="element-5896">
                      Clientes nuevos te descubren sin que hagas nada
                    </span>
                  </div>
                  <div className="flex items-start gap-2" data-id="element-5897">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" data-id="element-5898" />
                    <span className="text-sm text-white/70" data-id="element-5899">
                      Pedidos directos desde la página del marketplace
                    </span>
                  </div>
                  <div className="flex items-start gap-2" data-id="element-5900">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" data-id="element-5901" />
                    <span className="text-sm text-white/70" data-id="element-5902">
                      Sin comisiones extra — ya está incluido en tu plan
                    </span>
                  </div>
                </div>
                <div className="mt-4 p-3 bg-white/5 rounded-xl border border-white/5" data-id="element-5903">
                  <p className="text-xs text-white/50" data-id="element-5904">
                    <strong className="text-white/70" data-id="element-5905">Requisito:</strong> Para
                    aparecer en el marketplace, tu restaurante debe tener un
                    menú digital activo con NOIZA (cualquier plan). Así
                    garantizamos que todos los restaurantes listados ofrecen una
                    experiencia digital de calidad a los clientes.
                  </p>
                </div>
              </div>

              {/* CTA */}
              <div className="text-center pt-4" data-id="element-5906">
                <button onClick={() => {
              setShowHowItWorks(false);
              handleNext();
            }} className="px-8 py-4 bg-[#C9A34E] text-[#0B0B0B] rounded-2xl font-bold text-lg hover:bg-[#d4b365] transition-colors inline-flex items-center gap-2" data-id="element-5907">
                  Aplicar ahora <ChevronRight className="w-5 h-5" data-id="element-5908" />
                </button>
                <p className="text-white/40 text-xs mt-3" data-id="element-5909">
                  Aplicar es gratis y sin compromiso. Nuestro equipo te contacta
                  para explicarte todo.
                </p>
              </div>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
  const renderHero = () => <motion.div className="relative z-10 flex flex-col items-center justify-center min-h-screen px-4 text-center" initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0,
    y: -50
  }} data-id="element-5910">
      {/* Logo Area */}
      <motion.div className="mb-8 relative" initial={{
      scale: 0.8,
      opacity: 0
    }} animate={{
      scale: 1,
      opacity: 1
    }} transition={{
      duration: 0.8,
      ease: 'easeOut'
    }} data-id="element-5911">
        <div className="absolute inset-0 bg-[#C9A34E] blur-[60px] opacity-30 rounded-full" data-id="element-5912" />
        <h1 className="text-5xl md:text-7xl font-black text-white tracking-tighter relative z-10" style={{
        textShadow: '0 0 20px rgba(201,163,78,0.5)'
      }} data-id="element-5913">
          NOIZA
        </h1>
      </motion.div>

      {/* Headlines */}
      <motion.h2 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white max-w-4xl leading-tight mb-6" initial={{
      y: 20,
      opacity: 0
    }} animate={{
      y: 0,
      opacity: 1
    }} transition={{
      delay: 0.2,
      duration: 0.8
    }} data-id="element-5914">
        Tu restaurante ya no necesita solo un menú.{' '}
        <span className="text-[#C9A34E]" data-id="element-5915">Necesita un sistema.</span>
      </motion.h2>

      <motion.p className="text-lg md:text-xl text-white/70 max-w-2xl mb-12" initial={{
      y: 20,
      opacity: 0
    }} animate={{
      y: 0,
      opacity: 1
    }} transition={{
      delay: 0.4,
      duration: 0.8
    }} data-id="element-5916">
        Aplica para unirte al ecosistema digital más avanzado para restaurantes.
        Pedidos, operación, KDS y crecimiento en una sola plataforma.
      </motion.p>

      {/* CTAs */}
      <motion.div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto mb-16" initial={{
      y: 20,
      opacity: 0
    }} animate={{
      y: 0,
      opacity: 1
    }} transition={{
      delay: 0.6,
      duration: 0.8
    }} data-id="element-5917">
        <button onClick={handleNext} className="relative group px-8 py-4 bg-[#C9A34E] text-[#0B0B0B] rounded-2xl font-bold text-lg overflow-hidden transition-transform hover:scale-105" data-id="element-5918">
          <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-out" data-id="element-5919" />
          <span className="relative z-10 flex items-center justify-center gap-2" data-id="element-5920">
            Aplicar ahora <ChevronRight className="w-5 h-5" data-id="element-5921" />
          </span>
        </button>
        <button onClick={() => setShowHowItWorks(true)} className="px-8 py-4 bg-white/5 hover:bg-white/10 text-white border border-white/10 rounded-2xl font-bold text-lg transition-all backdrop-blur-sm" data-id="element-5922">
          Ver cómo funciona
        </button>
      </motion.div>

      {/* Trust Pills */}
      <motion.div className="flex flex-wrap justify-center gap-3 max-w-3xl" initial="hidden" animate="visible" variants={{
      visible: {
        transition: {
          staggerChildren: 0.1,
          delayChildren: 0.8
        }
      }
    }} data-id="element-5923">
        {['Pedidos por WhatsApp', 'Tracking en tiempo real', 'KDS y operación', 'Marketplace gastronómico', 'Tecnología para restaurantes'].map((pill, i) => <motion.div key={i} variants={{
        hidden: {
          opacity: 0,
          y: 10
        },
        visible: {
          opacity: 1,
          y: 0
        }
      }} className="px-4 py-2 rounded-full bg-white/5 border border-white/10 text-white/60 text-sm backdrop-blur-md" data-id="element-5924">
            {pill}
          </motion.div>)}
      </motion.div>
    </motion.div>;
  const renderFormStep = () => {
    const slideVariants = {
      enter: {
        x: 50,
        opacity: 0
      },
      center: {
        x: 0,
        opacity: 1
      },
      exit: {
        x: -50,
        opacity: 0
      }
    };
    return <div className="relative z-10 flex flex-col items-center justify-center min-h-screen px-4 py-12" data-id="element-5925">
        <motion.div className="w-full max-w-xl bg-[#141414]/80 backdrop-blur-2xl border border-white/10 rounded-3xl shadow-2xl overflow-hidden" initial={{
        scale: 0.9,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 200
      }} data-id="element-5926">
          {/* Progress Bar */}
          <div className="h-1.5 w-full bg-white/5" data-id="element-5927">
            <motion.div className="h-full bg-[#C9A34E]" initial={{
            width: 0
          }} animate={{
            width: `${step / 5 * 100}%`
          }} transition={{
            type: 'spring',
            damping: 20
          }} data-id="element-5928" />
          </div>

          <div className="p-6 md:p-10" data-id="element-5929">
            {/* Header */}
            <div className="flex items-center mb-8" data-id="element-5930">
              <button onClick={handleBack} className="p-2 -ml-2 text-white/50 hover:text-white transition-colors" data-id="element-5931">
                <ArrowLeft className="w-6 h-6" data-id="element-5932" />
              </button>
              <div className="ml-auto text-sm font-bold text-[#C9A34E]" data-id="element-5933">
                Paso {step} de 5
              </div>
            </div>

            {/* Content */}
            <AnimatePresence mode="wait" data-id="element-5934">
              <motion.div key={step} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
              type: 'spring',
              damping: 25,
              stiffness: 200
            }} className="min-h-[300px]" data-id="element-5935">
                {step === 1 && <div className="space-y-6" data-id="element-5936">
                    <h3 className="text-2xl font-bold text-white mb-6" data-id="element-5937">
                      Empecemos con lo básico
                    </h3>
                    <div data-id="element-5938">
                      <label className="block text-sm font-medium text-white/60 mb-2" data-id="element-5939">
                        Tu nombre completo
                      </label>
                      <input type="text" value={formData.owner_name} onChange={e => setFormData({
                    ...formData,
                    owner_name: e.target.value
                  })} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-4 text-white text-lg focus:outline-none focus:border-[#C9A34E] focus:ring-1 focus:ring-[#C9A34E] transition-all" placeholder="Ej. Juan Pérez" autoFocus data-id="element-5940" />
                    </div>
                    <div data-id="element-5941">
                      <label className="block text-sm font-medium text-white/60 mb-2" data-id="element-5942">
                        Nombre del restaurante
                      </label>
                      <input type="text" value={formData.restaurant_name} onChange={e => setFormData({
                    ...formData,
                    restaurant_name: e.target.value
                  })} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-4 text-white text-lg focus:outline-none focus:border-[#C9A34E] focus:ring-1 focus:ring-[#C9A34E] transition-all" placeholder="Ej. La Pizzería" data-id="element-5943" />
                    </div>
                  </div>}

                {step === 2 && <div className="space-y-6" data-id="element-5944">
                    <h3 className="text-2xl font-bold text-white mb-6" data-id="element-5945">
                      ¿Cómo te contactamos?
                    </h3>
                    <div data-id="element-5946">
                      <label className="block text-sm font-medium text-white/60 mb-2" data-id="element-5947">
                        Número de WhatsApp
                      </label>
                      <input type="tel" value={formData.whatsapp} onChange={e => setFormData({
                    ...formData,
                    whatsapp: e.target.value
                  })} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-4 text-white text-lg focus:outline-none focus:border-[#C9A34E] focus:ring-1 focus:ring-[#C9A34E] transition-all" placeholder="88888888" autoFocus data-id="element-5948" />
                    </div>
                    <div data-id="element-5949">
                      <label className="block text-sm font-medium text-white/60 mb-2" data-id="element-5950">
                        Instagram o Facebook (Opcional)
                      </label>
                      <input type="text" value={formData.social_handle} onChange={e => setFormData({
                    ...formData,
                    social_handle: e.target.value
                  })} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-4 text-white text-lg focus:outline-none focus:border-[#C9A34E] focus:ring-1 focus:ring-[#C9A34E] transition-all" placeholder="@turestaurante" data-id="element-5951" />
                    </div>
                  </div>}

                {step === 3 && <div className="space-y-6" data-id="element-5952">
                    <h3 className="text-2xl font-bold text-white mb-6" data-id="element-5953">
                      ¿Qué tipo de negocio tienes?
                    </h3>
                    <div className="grid grid-cols-2 gap-3" data-id="element-5954">
                      {(['Restaurante', 'Cafetería', 'Pizzería', 'Comida rápida', 'Panadería', 'Otro'] as BusinessType[]).map(type => <button key={type} onClick={() => setFormData({
                    ...formData,
                    business_type: type
                  })} className={`p-4 rounded-xl border text-left transition-all ${formData.business_type === type ? 'bg-[#C9A34E]/10 border-[#C9A34E] text-[#C9A34E]' : 'bg-white/5 border-white/10 text-white hover:bg-white/10'}`} data-id="element-5955">
                          <span className="font-medium" data-id="element-5956">{type}</span>
                        </button>)}
                    </div>
                  </div>}

                {step === 4 && <div className="space-y-8" data-id="element-5957">
                    <h3 className="text-2xl font-bold text-white mb-6" data-id="element-5958">
                      Operación actual
                    </h3>

                    <div data-id="element-5959">
                      <label className="block text-sm font-medium text-white/60 mb-3" data-id="element-5960">
                        ¿Ya reciben pedidos por WhatsApp?
                      </label>
                      <div className="grid grid-cols-2 gap-3" data-id="element-5961">
                        <button onClick={() => setFormData({
                      ...formData,
                      uses_whatsapp_orders: true
                    })} className={`p-4 rounded-xl border text-center transition-all ${formData.uses_whatsapp_orders === true ? 'bg-[#C9A34E]/10 border-[#C9A34E] text-[#C9A34E]' : 'bg-white/5 border-white/10 text-white hover:bg-white/10'}`} data-id="element-5962">
                          Sí
                        </button>
                        <button onClick={() => setFormData({
                      ...formData,
                      uses_whatsapp_orders: false
                    })} className={`p-4 rounded-xl border text-center transition-all ${formData.uses_whatsapp_orders === false ? 'bg-[#C9A34E]/10 border-[#C9A34E] text-[#C9A34E]' : 'bg-white/5 border-white/10 text-white hover:bg-white/10'}`} data-id="element-5963">
                          No
                        </button>
                      </div>
                    </div>

                    <div data-id="element-5964">
                      <label className="block text-sm font-medium text-white/60 mb-3" data-id="element-5965">
                        ¿Tienen menú digital hoy?
                      </label>
                      <div className="grid grid-cols-2 gap-3" data-id="element-5966">
                        <button onClick={() => setFormData({
                      ...formData,
                      has_digital_menu: true
                    })} className={`p-4 rounded-xl border text-center transition-all ${formData.has_digital_menu === true ? 'bg-[#C9A34E]/10 border-[#C9A34E] text-[#C9A34E]' : 'bg-white/5 border-white/10 text-white hover:bg-white/10'}`} data-id="element-5967">
                          Sí
                        </button>
                        <button onClick={() => setFormData({
                      ...formData,
                      has_digital_menu: false
                    })} className={`p-4 rounded-xl border text-center transition-all ${formData.has_digital_menu === false ? 'bg-[#C9A34E]/10 border-[#C9A34E] text-[#C9A34E]' : 'bg-white/5 border-white/10 text-white hover:bg-white/10'}`} data-id="element-5968">
                          No
                        </button>
                      </div>
                    </div>
                  </div>}

                {step === 5 && <div className="space-y-6" data-id="element-5969">
                    <h3 className="text-2xl font-bold text-white mb-2" data-id="element-5970">
                      ¿Qué les gustaría mejorar primero?
                    </h3>
                    <p className="text-white/50 text-sm mb-6" data-id="element-5971">
                      Puedes seleccionar varias opciones
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-5972">
                      {(['Más pedidos', 'Mejor control de cocina', 'Menú digital', 'Delivery / express', 'Orden y operación', 'Todo lo anterior'] as MainGoal[]).map(goal => {
                    const isSelected = formData.main_goal.includes(goal);
                    return <button key={goal} onClick={() => {
                      if (goal === 'Todo lo anterior') {
                        setFormData({
                          ...formData,
                          main_goal: ['Todo lo anterior']
                        });
                      } else {
                        const newGoals = isSelected ? formData.main_goal.filter(g => g !== goal) : [...formData.main_goal.filter(g => g !== 'Todo lo anterior'), goal];
                        setFormData({
                          ...formData,
                          main_goal: newGoals
                        });
                      }
                    }} className={`p-4 rounded-xl border text-left transition-all ${isSelected ? 'bg-[#C9A34E]/10 border-[#C9A34E] text-[#C9A34E]' : 'bg-white/5 border-white/10 text-white hover:bg-white/10'}`} data-id="element-5973">
                            <span className="font-medium text-sm" data-id="element-5974">{goal}</span>
                          </button>;
                  })}
                    </div>
                  </div>}
              </motion.div>
            </AnimatePresence>

            {/* Footer Actions */}
            <div className="mt-10 pt-6 border-t border-white/10" data-id="element-5975">
              {error && <p className="text-red-400 text-sm mb-4 text-center" data-id="element-5976">{error}</p>}

              {step < 5 ? <button onClick={handleNext} disabled={!isStepValid()} className="w-full py-4 bg-[#C9A34E] text-[#0B0B0B] rounded-xl font-bold text-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#d4b365]" data-id="element-5977">
                  Continuar
                </button> : <button onClick={handleSubmit} disabled={!isStepValid() || isSubmitting} className="w-full py-4 bg-[#C9A34E] text-[#0B0B0B] rounded-xl font-bold text-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#d4b365] flex items-center justify-center gap-2" data-id="element-5978">
                  {isSubmitting ? <>
                      <Loader2 className="w-5 h-5 animate-spin" data-id="element-5979" /> Enviando...
                    </> : 'Enviar solicitud'}
                </button>}
            </div>
          </div>
        </motion.div>
      </div>;
  };
  const renderSuccess = () => <div className="relative z-10 flex flex-col items-center justify-center min-h-screen px-4 text-center" data-id="element-5980">
      <motion.div className="w-full max-w-md bg-[#141414]/80 backdrop-blur-2xl border border-white/10 rounded-3xl p-10 shadow-2xl" initial={{
      scale: 0.9,
      opacity: 0
    }} animate={{
      scale: 1,
      opacity: 1
    }} transition={{
      type: 'spring',
      damping: 25,
      stiffness: 200
    }} data-id="element-5981">
        <motion.div className="w-20 h-20 bg-[#C9A34E]/20 rounded-full flex items-center justify-center mx-auto mb-6" initial={{
        scale: 0
      }} animate={{
        scale: 1
      }} transition={{
        delay: 0.2,
        type: 'spring'
      }} data-id="element-5982">
          <CheckCircle2 className="w-10 h-10 text-[#C9A34E]" data-id="element-5983" />
        </motion.div>

        <h2 className="text-3xl font-bold text-white mb-4" data-id="element-5984">
          Recibimos tu solicitud
        </h2>
        <p className="text-white/70 mb-8 leading-relaxed" data-id="element-5985">
          Si tu restaurante encaja con el ecosistema NOIZA, nuestro equipo se
          pondrá en contacto contigo muy pronto.
        </p>

        <a href="https://wa.me/50688888888" target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 w-full py-4 bg-[#25D366] text-white rounded-xl font-bold text-lg hover:bg-[#20bd5a] transition-colors" data-id="element-5986">
          <MessageCircle className="w-5 h-5" data-id="element-5987" />
          Hablar con NOIZA
        </a>
      </motion.div>
    </div>;
  return <div className="min-h-screen bg-[#0B0B0B] font-sans selection:bg-[#C9A34E]/30 text-white" data-id="element-5988">
      {renderFloatingBackground()}
      {renderHowItWorksModal()}

      <AnimatePresence mode="wait" data-id="element-5989">
        {step === 0 && <motion.div key="hero" className="w-full" data-id="element-5990">
            {renderHero()}
          </motion.div>}
        {step > 0 && step < 6 && <motion.div key="form" className="w-full" data-id="element-5991">
            {renderFormStep()}
          </motion.div>}
        {step === 6 && <motion.div key="success" className="w-full" data-id="element-5992">
            {renderSuccess()}
          </motion.div>}
      </AnimatePresence>
    </div>;
}